import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Send, Layers, UploadCloud, History, CheckCircle2, ShieldCheck, Headphones, Eye, FileArchive, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GLOBAL = {
  conforme: { label: 'Conforme', cls: 'text-emerald-600', bg: 'bg-emerald-50' },
  non_conforme: { label: 'Non conforme', cls: 'text-red-600', bg: 'bg-red-50' },
  a_verifier: { label: 'À vérifier', cls: 'text-amber-600', bg: 'bg-amber-50' },
};

const Circle = ({ pct }) => {
  const r = 34, c = 2 * Math.PI * r;
  const off = c - (pct / 100) * c;
  const color = pct >= 100 ? '#059669' : pct > 0 ? '#0d9488' : '#e5e7eb';
  return (
    <div className="relative w-[88px] h-[88px]">
      <svg width="88" height="88" className="-rotate-90">
        <circle cx="44" cy="44" r={r} stroke="#eef2f5" strokeWidth="8" fill="none" />
        <circle cx="44" cy="44" r={r} stroke={color} strokeWidth="8" fill="none"
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" style={{ transition: 'stroke-dashoffset .5s ease' }} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-lg font-bold text-gray-800">{pct}%</span>
    </div>
  );
};

const Legend = ({ color, label, value }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="flex items-center gap-2 text-gray-600"><span className={`w-2 h-2 rounded-full ${color}`} />{label}</span>
    <span className="font-semibold text-gray-800">{value}</span>
  </div>
);

const QuickAction = ({ icon: Icon, label, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 text-sm text-gray-700 transition-colors text-left">
    <Icon className="w-4 h-4 text-emerald-600 shrink-0" /> {label}
  </button>
);

const DocumentsComplianceRail = ({ stats, operation = {}, operationId, onShowMissing, onRequestField, onImportMultiple, onHistory }) => {
  const g = GLOBAL[stats.globalStatus] || GLOBAL.non_conforme;
  const [zipBusy, setZipBusy] = useState(false);

  const exportZip = async () => {
    const opId = operationId || operation?.id;
    if (!opId) return;
    setZipBusy(true);
    try {
      const res = await axios.get(`${API}/operations/${opId}/documents/export-zip`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `documents_${operation?.operationNumber || operation?.runNumber || opId}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Archive ZIP téléchargée');
    } catch (e) {
      const msg = e.response?.status === 404 ? 'Aucun document à exporter' : 'Export impossible';
      toast.error(msg);
    } finally { setZipBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="documents-compliance-rail">
      {/* Conformité documents */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Conformité documents</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-4">
            <Circle pct={stats.progress} />
            <div className="flex-1 space-y-1">
              <Legend color="bg-emerald-500" label="Reçus" value={stats.recus} />
              <Legend color="bg-red-500" label="Manquants" value={stats.manquants} />
              <Legend color="bg-blue-500" label="Validés" value={stats.valides} />
              <Legend color="bg-amber-500" label="À vérifier" value={stats.aVerifier} />
              <Legend color="bg-rose-600" label="Refusés" value={stats.refuses} />
            </div>
          </div>
          <div className={`flex items-center justify-between rounded-lg px-3 py-2 ${g.bg}`}>
            <span className="text-sm text-gray-500">Statut</span>
            <span className={`text-sm font-semibold ${g.cls}`}>{g.label}</span>
          </div>
          <Button variant="outline" className="w-full" onClick={onShowMissing} data-testid="rail-show-missing">
            <Eye className="w-4 h-4 mr-1.5" /> Voir les documents manquants
          </Button>
        </CardContent>
      </Card>

      {/* Actions rapides */}
      <Card>
        <CardHeader className="pb-1"><CardTitle className="text-base">Actions rapides</CardTitle></CardHeader>
        <CardContent className="p-2">
          <QuickAction icon={Send} label="Demander au terrain" onClick={onRequestField} testid="rail-request-field" />
          <QuickAction icon={Layers} label="Générer demande multiple" onClick={onRequestField} testid="rail-bulk-request" />
          <QuickAction icon={UploadCloud} label="Import multiple" onClick={onImportMultiple} testid="rail-import-multiple" />
          <button onClick={exportZip} disabled={zipBusy} data-testid="rail-export-zip"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 text-sm text-gray-700 transition-colors text-left disabled:opacity-60">
            {zipBusy ? <Loader2 className="w-4 h-4 text-emerald-600 shrink-0 animate-spin" /> : <FileArchive className="w-4 h-4 text-emerald-600 shrink-0" />}
            Télécharger tout (ZIP)
          </button>
          <QuickAction icon={History} label="Historique documents" onClick={onHistory} testid="rail-history" />
        </CardContent>
      </Card>

      {/* Règles de conformité */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Règles de conformité</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <p className="text-xs text-gray-500">L'opération n'est conforme que si tous les documents obligatoires sont :</p>
          {['Présents', 'Valables', 'Lisibles', 'Conformes'].map((r) => (
            <div key={r} className="flex items-center gap-2 text-sm text-gray-700">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" /> {r}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Besoin d'aide */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Besoin d'aide ?</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-gray-500">Contactez le support Ops pour toute assistance.</p>
          <Button variant="outline" className="w-full" onClick={() => { const t = document.querySelector('button[value="messages"]'); t?.click(); }}>
            <Headphones className="w-4 h-4 mr-1.5" /> Contacter le support
          </Button>
        </CardContent>
      </Card>

      {/* Informations opération */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Informations opération</CardTitle></CardHeader>
        <CardContent className="space-y-2.5 text-sm">
          <div>
            <p className="text-xs text-gray-400">Trajet</p>
            <p className="text-gray-800">{(operation.departure || '—').split(',')[0]} → {(operation.arrival || '—').split(',')[0]}</p>
          </div>
          {operation.distance != null && <Row k="Distance" v={`${operation.distance} km`} />}
          {operation.vehicleType && <Row k="Véhicule" v={operation.vehicleType} />}
          {operation.merchandiseType && <Row k="Marchandise" v={operation.merchandiseType} />}
          {operation.assignedFieldOpsName && <Row k="Field Ops" v={operation.assignedFieldOpsName} />}
          {operation.createdAt && <Row k="Date de création" v={new Date(operation.createdAt).toLocaleDateString('fr-FR')} />}
        </CardContent>
      </Card>
    </div>
  );
};

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between">
    <span className="text-gray-400 text-xs">{k}</span>
    <span className="text-gray-800 font-medium text-right">{v}</span>
  </div>
);

export default DocumentsComplianceRail;
